import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

import {
  Tabs,
  DropDownSelector,
  RangeBox,
  ButtonBasic,
  CardStatistics,
  DropContainer,
  TableBox,
  MetricsItem,
  NoDataContent,
  Sceleton,
} from '@lk-gtcom/ecomlab-components';

import customHeaders, { getSpace } from '../../common/headers';
import { useChartData, useDataTable } from '../../common/hooks';
import {
  fetchFilterV3,
  getValueMultiFilter,
  RequestController,
} from '../../common/filtersFunc';
import { periodWeek, msToLocalDate } from '../../common/constant';
import { options_prop_mp } from '../../common/globalVariables';
import { useURLSearchParams } from '../../common/useURLSearchParams';
import { KonstantinMasterHttps } from '../../fetchUrls';
import { useResponseHandler } from '../../common/auth';
import { LineChart } from '../../charts/LineChart/LineChart';
import { ModalReview } from '../../components/Modal/ModalReview/ModalReview';
import { FilterContainer } from '../../components/FilterContainer/FilterContainer';
import { FilterArticles } from '../../components/FilterArticles/FilterArticles';
import { TitlePage } from '../../components/TitlePage/TitlePage';
import { AdvancedAnalytics } from '../AdvancedAnalytics/AdvancedAnalytics';
import { AdvancedAnalyticsQuestions } from '../AdvancedAnalyticsQuestions/AdvancedAnalyticsQuestions';
import useGeneralStore from '../../store/general';
import { useSavedFilters } from '../../common/useSavedFilters';

import banner_no_data from './img/banner-no-data.svg';
import info_icon from './img/info-blue.svg';

import './AnaliticsReview.scss';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const btn_tab = [
  { label: 'Товары', value: 'by-product' },
  { label: 'По категориям', value: 'by-category' },
  { label: 'По брендам', value: 'by-brand' },
  { label: 'Категоризация отзывов', value: 'categorization' },
  { label: 'Аналитика', value: 'analytics' },
];

const metrics_names_feedbacks = {
  'by-product': [
    { label: '', path: 'feedback_number' },
    { label: '', path: 'avg_rating' },
    { label: '', path: 'photo_number' },
    { label: '', path: 'avg_feedbacks_per_day' },
    { label: '', path: 'products_number' },
    { label: '', path: 'unanswered_feedback_number' },
  ],
  'by-category': [
    { label: 'Средний рейтинг', path: 'feedback_number' },
    { label: 'Топ 5 категорий по кол-ву отзывов', path: 'products_number' },
    { label: 'Оценки', path: 'avg_rating' },
    { label: 'Оценки', path: 'unanswered_feedback_number' },
  ],
  'by-brand': [
    { label: 'Средний рейтинг', path: 'feedback_number' },
    { label: 'Топ 5 категорий по кол-ву отзывов', path: 'products_number' },
    { label: 'Оценки', path: 'avg_rating' },
    { label: 'Оценки', path: 'unanswered_feedback_number' },
  ],
  categorization: [
    { label: '', path: 'feed-categories' },
    { label: '', path: 'feedbacks' },
    { label: '', path: 'product-valuation' },
    { label: '', path: 'good-values' },
    { label: '', path: 'bad-values' },
    { label: '', path: 'items-count' },
  ],
};

const metrics_names_questions = [
  'question_number',
  'products_number',
  'avg_answer_time',
  'unanswered_question_number',
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AnaliticsReview = ({
  openConnectionModalById,
  setIsModalKnowledgeBase,
  pageValue,
  setQueryFeedbacks,
}) => {
  const selectedSpace = useGeneralStore((state) => state.selectedSpace);
  const headers = { ...customHeaders, ...getSpace(selectedSpace) };
  const { handleStatusCode, handleTableResponse, handleRequestError } = useResponseHandler();
  // Сохранённые фильтры: подставляются в URL при заходе, запись по кнопке
  const { isReady, saveFilters, clearSavedFilters, isSaved, hasSavedFilters } = useSavedFilters(selectedSpace?.value, `${pageValue}-page`);


  // Реф для хранения контроллера запросов
  const requestControllerRef = useRef(new RequestController());

  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = useParams()['*'];
  const [searchParam, setSearchParam] = useSearchParams();

  // ---- Период -----------------------------------------------------------
  const [date, setDate] = useState(periodWeek());
  const [date_from, date_to] = date;
  const [dateFrom, dateTo] = date;
  const [allPeriod, setAllPeriod] = useState(false);

  // ---- Общее состояние страницы -----------------------------------------
  const [isNotOneAccount, setIsNotOneAccount] = useState('done');
  const [pageContent, setPageContent] = useState('by-product');
  const [sort, setSort] = useState([]);
  const [status, setStatus] = useState('true');
  const [checkedKeywords, setCheckedKeywords] = useState([]);
  const [questData, setQuestData] = useState({});
  const [isModal, setIsModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  // ---- Метрики ------------------------------------------------------------
  const [metrics, setMetrics] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [fetchedCharts, setFetchedCharts] = useState([]);
  const [fetchedMetrics, setFetchedMetrics] = useState([]);
  const [loadMetrics, setLoadMetrics] = useState(false);

  // ---- График --------------------------------------------------------------
  const [setChartData, chartData, setChartFuncs] = useChartData();
  const { setChartLoading, resetChart } = setChartFuncs;

  // ---- Таблица --------------------------------------------------------------
  const [isTableRequest, setIsTableRequest] = useState(false);
  const paginatorRef = useRef();
  const [setTable, tableProps, setTableFuncs] = useDataTable(
    paginatorRef.current?.setTotal,
    paginatorRef
  );
  const { setLoading } = setTableFuncs;

  // ---- Фильтры: конфигурация и URL-синхронизация ---------------------------
  const filterConfig = [
    { key: 'mp', type: 'multi' },
    { key: 'api', type: 'multi' },
    { key: 'category', type: 'multi' },
    { key: 'brand', type: 'multi' },
    { key: 'date_from', type: 'date' },
    { key: 'date_to', type: 'date' },
  ];
  const { urlParams, updateURL, isInitialized, hasInitialParams } =
    useURLSearchParams(filterConfig);

  const [isFilterRequest, setIsFilterRequest] = useState(false);
  const [filterValue, setFilterValue] = useState([]);

  // ---- Фильтры: данные списков (API/категория/бренд) -----------------------
  const [apiData, setApiData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [brandData, setBrandData] = useState([]);

  const [selectedApi, setSelectedApi] = useState([{ label: 'Все', value: 'all' }]);
  const [selectedCategory, setSelectedCategory] = useState([{ label: 'Все', value: 'all' }]);
  const [selectedBrand, setSelectedBrand] = useState([{ label: 'Все', value: 'all' }]);
  const [selectedMarketplace, setSelectedMarketplace] = useState([{ label: 'Все', value: 'all' }]);

  const [totalApi, setTotalApi] = useState(0);
  const [totalCategory, setTotalCategory] = useState(0);
  const [totalBrand, setTotalBrand] = useState(0);

  const [apiPage, setApiPage] = useState(1);
  const [categoryPage, setCategoryPage] = useState(1);
  const [brandPage, setBrandPage] = useState(1);
  const [showDropBox, setShowDropBox] = useState(true);

  const [apiQuery, setApiQuery] = useState('');
  const [categoryQuery, setCategoryQuery] = useState('');
  const [brandQuery, setBrandQuery] = useState('');

  // Инициализация фильтров из URL параметров при первом рендере
  const [isInitialLoadFromURL, setIsInitialLoadFromURL] = useState(false);

  // ---------------------------------------------------------------------
  // Эффекты: инициализация и синхронизация URL
  // ---------------------------------------------------------------------

  useEffect(() => {
    if (!isReady) {
      setIsInitialLoadFromURL(false);
      return;
    }
    if (!isInitialLoadFromURL) {
      setIsInitialLoadFromURL(true);

      // Обновляем все состояния фильтров
      setTimeout(() => {
        if (urlParams.mp) setSelectedMarketplace(urlParams.mp);
        if (urlParams.api) setSelectedApi(urlParams.api);
        if (urlParams.category) setSelectedCategory(urlParams.category);
        if (urlParams.brand) setSelectedBrand(urlParams.brand);
        if (urlParams.date_from) {
          setDate([
            new Date(parseInt(urlParams.date_from)),
            new Date(parseInt(urlParams.date_to)),
          ]);
        }

        // Запускаем запрос таблицы и фильтров только при первой инициализации из URL
        setIsFilterRequest((prev) => !prev);
        if (Object.keys(urlParams).length > 0) {
          setIsTableRequest((prev) => !prev);
        }
      }, 10);
    }
  }, [urlParams, isReady]);

  useEffect(() => {
    if (!isReady) return;
    updateURL({
      mp: urlParams?.mp || selectedMarketplace,
      api: urlParams?.api || selectedApi,
      category: urlParams?.category || selectedCategory,
      brand: urlParams?.brand || selectedBrand,
      date_from: urlParams?.date_from || dateFrom,
      date_to: urlParams?.date_to || dateTo,
    });
  }, [date, isReady]);

  useEffect(() => {
    setQueryFeedbacks(window?.location?.search);
  }, [window?.location?.search]);

  useEffect(() => {
    setPageContent(activeTab);
  }, [activeTab]);

  const resetTable = () => {
    setSort({});
  };

  // ---------------------------------------------------------------------
  // Запросы данных: таблица
  // ---------------------------------------------------------------------

  const fetchTableData = (params, path, label, abortController = new AbortController()) => {
    setLoading(true);
    if (pageContent == 'analytics') return

    let sort;
    let filtersParam = [];

    if (params) {
      [sort, filtersParam] = params;
    }

    let url = `${KonstantinMasterHttps}/wb/${pageValue}/analytics/${pageContent}/table${pageContent === 'advanced' ? `s/${path}` : ''
      }${sort?.length > 0 ? '?order_param=' + sort[0] + '&order_param_direction=' + sort[1] : ''}`;

    let keyword_list = checkedKeywords;

    const body = JSON.stringify({
      page: paginatorRef.current?.page,
      limit: paginatorRef.current?.limit,
      filters: filtersParam,
      mp_id_list: getValueMultiFilter(selectedMarketplace),
      api_id_list: getValueMultiFilter(selectedApi),
      category_id_list: getValueMultiFilter(selectedCategory),
      brand_id_list: getValueMultiFilter(selectedBrand),
      date_from: allPeriod ? null : msToLocalDate(dateFrom),
      date_to: allPeriod ? null : msToLocalDate(dateTo),
      positive: status === 'true',
      keyword_list,
      article_filter: filterValue,
    });

    fetch(url, { body, method: 'POST', headers, signal: abortController.signal })
      .then(handleTableResponse)
      .then((json) => setTable(json))
      .catch(handleRequestError)
      .finally(() => {
        if (abortController?.signal.aborted) return;
        setLoading(false);
      });
  };

  // ---------------------------------------------------------------------
  // Запросы данных: список метрик
  // ---------------------------------------------------------------------

  const fetchMetricsList = () => {
    setMetrics([]);
    if (pageContent == 'analytics') return

    let url = `${KonstantinMasterHttps}/wb/${pageValue}/analytics/${pageContent}/${pageContent === 'categorization' ? 'plots' : 'plots'
      }/metric-list`;

    fetch(url, { method: 'GET', headers: headers })
      .then(handleTableResponse)
      .then((data) => {
        setMetrics(data);
        setSelectedMetric(data[0]?.value);
      })
      .catch(handleRequestError);
  };

  // ---------------------------------------------------------------------
  // Запросы данных: метрики (карточки)
  // ---------------------------------------------------------------------

  const fetchMetricData = (path, abortController = new AbortController()) => {
    setLoadMetrics(true);
    setFetchedMetrics([]);

    let url = `${KonstantinMasterHttps}/wb/${pageValue}/analytics/${pageValue != 'questions' ? pageContent + '/' : ''}metrics/${path}`;

    const body = JSON.stringify({
      api_id_list: getValueMultiFilter(selectedApi),
      category_id_list: getValueMultiFilter(selectedCategory),
      brand_id_list: getValueMultiFilter(selectedBrand),
      date_from: allPeriod ? null : msToLocalDate(date_from),
      date_to: allPeriod ? null : msToLocalDate(date_to),
    });

    fetch(url, { body, method: 'POST', headers, signal: abortController.signal })
      .then(handleTableResponse)
      .then((json) => setFetchedMetrics((prev) => [...prev, json]))
      .catch(handleRequestError)
      .finally(() => {
        if (abortController.signal.aborted) {
          setFetchedMetrics([]);
          return;
        }
        setLoadMetrics(false);
      });
  };

  // ---------------------------------------------------------------------
  // Запросы данных: график
  // ---------------------------------------------------------------------

  const fetchChartData = async (abortController = new AbortController()) => {
    if (!selectedMetric) return;
    if (selectedItems?.length < 1) return;
    setChartLoading(true);

    let url = `${KonstantinMasterHttps}/wb/${pageValue}/analytics/${pageContent}/${pageContent === 'categorization' ? 'plots' : 'plots'
      }`;

    const [date_from, date_to] = date;

    let searchId;
    const id_arr = selectedItems?.map((el) => {
      if (el) {
        if (pageContent === 'by-product' && el?.['Товар']) {
          if (Array.isArray(el?.['Товар'])) {
            return el?.['Товар'][4];
          }
        }

        if (pageContent === 'by-category') {
          return el?.category_id;
        }

        if (pageContent === 'by-brand') {
          return el?.brand_id;
        }

        if (pageContent === 'categorization') {
          if (el?.['Категория']) {
            return el?.['Категория'];
          }
        }
      }
    });

    if (pageContent === 'by-product') {
      searchId = { nm_id_list: id_arr };
    }
    if (pageContent === 'by-category') {
      searchId = { category_id_list: id_arr };
    }
    if (pageContent === 'by-brand') {
      searchId = { brand_id_list: id_arr };
    }
    if (pageContent === 'categorization') {
      searchId = { primary_category_list: id_arr };
    }

    const body = JSON.stringify({
      mp_id_list: getValueMultiFilter(selectedMarketplace),
      api_id_list: getValueMultiFilter(selectedApi),
      date_from: allPeriod ? null : msToLocalDate(date_from),
      date_to: allPeriod ? null : msToLocalDate(date_to),
      metric_name: selectedMetric,
      ...searchId,
    });

    await fetch(url, {
      body,
      method: 'POST',
      headers,
      signal: abortController.signal,
    })
      .then(handleTableResponse)
      .then((json) => setChartData(json))
      .catch(handleCheck)
      .finally(() => {
        if (abortController.signal.aborted) {
          setFetchedCharts([]);
          return;
        }
        setChartLoading(false);
      });
  };

  useEffect(() => {
    fetchMetricsList();
  }, [pageValue, pageContent]);

  useEffect(() => {
    fetchChartData();
  }, [selectedMetric, selectedItems]);

  // ---------------------------------------------------------------------
  // Обработчики
  // ---------------------------------------------------------------------

  const onRedirect = (e) => {
    const [dataObj, el, header] = e;
    const [name] = el;
    const { api_id } = dataObj || {};

    if (header === 'Категория') {
      navigate(`/work-with-review/feedbacks?category=${name}`);
    }

    if (header === 'Бренд') {
      navigate(`/work-with-review/feedbacks?brand=${name}`);
    }

    if (header === 'Магазин') {
      navigate(`/work-with-review/feedbacks?apiName=${name}&apiId=${api_id}`);
    }
  };

  const handleCheck = (data) => {
    setSelectedItems(data);
  };

  // ---------------------------------------------------------------------
  // Запросы данных: списки для фильтров (API / категория / бренд)
  // ---------------------------------------------------------------------

  const filtersApi = () => {
    const controller = requestControllerRef.current.createController('api');

    fetchFilterV3({
      selectedSpace: selectedSpace?.value,
      selectedMP: selectedMarketplace,
      page: apiPage,
      set: setApiData,
      searchParam: apiQuery,
      controller,
      setTotal: setTotalApi,
      dateFrom,
      dateTo,
      filter: 'fq_api_ids',
    })?.finally(() => {
      requestControllerRef.current.removeController('api');
    });
  };

  const filtersCategory = () => {
    const controller = requestControllerRef.current.createController('category');

    fetchFilterV3({
      selectedSpace: selectedSpace?.value,
      selectedMP: selectedMarketplace,
      selectedApi,
      page: categoryPage,
      set: setCategoryData,
      searchParam: categoryQuery,
      controller,
      setTotal: setTotalCategory,
      dateFrom,
      dateTo,
      filter: pageValue === 'feedbacks' ? 'feedbacks_categories' : 'questions_categories_new',
    })?.finally(() => {
      requestControllerRef.current.removeController('category');
    });
  };

  const filtersBrand = () => {
    const controller = requestControllerRef.current.createController('brand');

    fetchFilterV3({
      selectedSpace: selectedSpace?.value,
      selectedMP: selectedMarketplace,
      page: brandPage,
      selectedApi,
      selectedCategory,
      searchParam: brandQuery,
      set: setBrandData,
      controller,
      setTotal: setTotalBrand,
      dateFrom,
      dateTo,
      filter: pageValue === 'feedbacks' ? 'feedbacks_brands' : 'questions_brands_new',
    })?.finally(() => {
      requestControllerRef.current.removeController('brand');
    });
  };

  useEffect(() => {
    if (!isInitialLoadFromURL) return;
    filtersApi();
  }, [isFilterRequest, apiPage, apiQuery, pageValue]);

  useEffect(() => {
    if (!isInitialLoadFromURL) return;
    filtersCategory();
  }, [isFilterRequest, dateFrom, dateTo, categoryPage, categoryQuery, pageValue]);

  useEffect(() => {
    if (!isInitialLoadFromURL) return;
    filtersBrand();
  }, [isFilterRequest, dateFrom, dateTo, brandPage, brandQuery, pageValue]);

  // ---------------------------------------------------------------------
  // Эффекты: перезагрузка таблицы и метрик при смене фильтров/периода
  // ---------------------------------------------------------------------

  useEffect(() => {
    setFetchedMetrics([]);
    setFetchedCharts([]);

    const abortController = new AbortController();

    resetTable();
    fetchTableData([], true, null, abortController);

    if (pageValue != 'questions') {
      metrics_names_feedbacks[pageContent]?.map(({ path }) => fetchMetricData(path, abortController));
    } else {
      metrics_names_questions?.map((el) => fetchMetricData(el, abortController));
    }

    return () => abortController.abort();
  }, [
    date,
    isTableRequest,
    pageValue,
    status,
    pageContent,
    date,
    allPeriod,
    filterValue,
  ]);

  useEffect(() => {
    resetChart();
    setFetchedMetrics([]);
  }, [
    pageValue,
    pageContent,
    selectedApi,
    selectedBrand,
    selectedCategory,
    selectedMarketplace,
  ]);

  const commonProps = {
    date,
    selectedBrand,
    selectedCategory,
    selectedApi,
    selectedMarketplace,
    pageValue,
    pageContent,
    allPeriod,
  };

  return (
    <>
      {isModal && (
        <ModalReview
          pageValue={pageValue}
          questData={questData}
          setQuestData={(e) => setQuestData(e)}
          setIsModal={(e) => setIsModal(e)}
        />
      )}

      <div className="analitics-review">
        <div className="analitics-review__content">
          <header className="analitics-review__header">
            <TitlePage
              title={`Аналитика по ${pageValue === 'feedbacks' ? 'отзывам' : 'вопросам'}`}
            />
            <button
              className="btn_info"
              onClick={(e) => {
                setSearchParam({
                  content: pageContent,
                  'level-id': 67,
                  'article-id': 735,
                });
                setIsModalKnowledgeBase(true);
              }}
            >
              Что это такое? <img src={info_icon} />
            </button>
          </header>

          <div className="analitics-review__toolbar-top">
            <FilterContainer
              loading={isSaved}
              saveFilters={saveFilters}
              clearSavedFilters={clearSavedFilters}
              hasSavedFilters={hasSavedFilters}
              onReset={(e) => {
                setSelectedMarketplace([]);
                setSelectedApi([]);
                setSelectedCategory([]);
                setSelectedBrand([]);
                updateURL({
                  mp: null,
                  api: null,
                  category: null,
                  brand: null,
                });
                setIsTableRequest(!isTableRequest);
                setIsFilterRequest(!isFilterRequest);
              }}
            >
              <DropDownSelector
                options_prop={options_prop_mp}
                state={selectedMarketplace}
                setState={setSelectedMarketplace}
                placeholder="Маркетплейс"
                total={options_prop_mp?.length}
                multi
                onBlur={() => {
                  updateURL({ mp: selectedMarketplace });
                  filtersApi();
                  filtersCategory();
                  filtersBrand();
                  setIsTableRequest(!isTableRequest);
                }}
              />

              <DropDownSelector
                options_prop={apiData}
                state={selectedApi}
                setState={setSelectedApi}
                placeholder="Магазин"
                total={totalApi}
                fetchCallback={setApiPage}
                setInputChange={setApiQuery}
                multi
                onBlur={() => {
                  updateURL({ api: selectedApi });
                  filtersCategory();
                  filtersBrand();
                  setIsTableRequest(!isTableRequest);
                }}
              />

              <DropDownSelector
                options_prop={categoryData}
                state={selectedCategory}
                setState={setSelectedCategory}
                placeholder="Категория"
                total={totalCategory}
                fetchCallback={setCategoryPage}
                setInputChange={setCategoryQuery}
                multi
                onBlur={() => {
                  updateURL({ category: selectedCategory });
                  filtersBrand();
                  setIsTableRequest(!isTableRequest);
                }}
              />

              <DropDownSelector
                options_prop={brandData}
                state={selectedBrand}
                setState={setSelectedBrand}
                placeholder="Бренд"
                total={totalBrand}
                fetchCallback={setBrandPage}
                setInputChange={setBrandQuery}
                multi
                onBlur={() => {
                  updateURL({ brand: selectedBrand });
                  setIsTableRequest(!isTableRequest);
                }}
              />
            </FilterContainer>

            <RangeBox
              date={date}
              allPeriod={allPeriod}
              setAllPeriod={(e) => setAllPeriod(e)}
              setDate={(e) => {
                const [newDateFrom, newDateTo] = e;
                updateURL({
                  date_from: newDateFrom,
                  date_to: newDateTo,
                });
                setDate(e);
              }}
              disabled={isNotOneAccount !== 'done'}
            />
          </div>

          <Tabs
            query={location?.search}
            disabled={isNotOneAccount !== 'done'}
            tabs={btn_tab}
            pageValue={pageContent}
            spanTab
            setPageValue={setPageContent}
          />

          {isNotOneAccount === 'waiting' ? (
            <NoDataContent
              img={banner_no_data}
              title={'Подключение создано'}
              description={`Сбор и обработка информации займет до 24 часов.`}
            />
          ) : isNotOneAccount === 'none' ? (
            <NoDataContent
              img={banner_no_data}
              title="Нет активных подключений"
              description="Создайте подключение чтобы пользоваться всеми инструментами сервиса"
              child={
                <ButtonBasic
                  green
                  onClick={(e) => {
                    openConnectionModalById([1, 3]);
                  }}
                  text="Новое подключение"
                  size="56px"
                />
              }
            />
          ) : (
            <>
              {pageContent !== 'analytics' && (
                <>
                  <DropContainer
                    name={'Динамика показателей за выбранный период'}
                    showDropBox={showDropBox}
                    setShowDropBox={(e) => setShowDropBox(e)}
                  >
                    <div className="analitics-review__chart-and-metrics">
                      <div className="analitics-review__chart-group">
                        <LineChart
                          {...chartData}
                          legend={metrics}
                          selectedMetric={selectedMetric}
                          setSelectedMetric={setSelectedMetric}
                          metricsList={metrics}
                        />
                      </div>

                      <div className="analitics-review__metrics-group">
                        {loadMetrics
                          ? [...Array(6)].map((el, ind) => (
                            <Sceleton width={'216px'} height={'80px'} key={ind} />
                          ))
                          : fetchedMetrics?.length > 0
                            ? fetchedMetrics?.map((el, ind) => {
                              const { labels, data } = el;
                              return <MetricsItem key={ind + labels} data={data} {...labels} />;
                            })
                            : [...Array(6)]?.map((e, i) => (
                              <CardStatistics key={i} title="" value="" />
                            ))}
                      </div>
                    </div>
                  </DropContainer>

                  <TableBox
                    {...tableProps}
                    ref={paginatorRef}
                    onRedirectInfo
                    paginator={true}
                    onRedirect={onRedirect}
                    fetchCallback={fetchTableData}
                    onCheck={(e) => handleCheck(e)}
                    onUncheckAllDependence={[
                      pageValue,
                      pageContent,
                      selectedApi,
                      selectedBrand,
                      selectedCategory,
                      selectedMarketplace,
                      date,
                    ]}
                    child={
                      <FilterArticles
                        filterValue={filterValue}
                        setFilterValue={setFilterValue}
                      />
                    }
                  />
                </>
              )}

              {pageContent === 'analytics' && (
                <>
                  {pageValue === 'feedbacks' ? (
                    <AdvancedAnalytics {...commonProps} />
                  ) : (
                    <AdvancedAnalyticsQuestions {...commonProps} />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export { AnaliticsReview };
